import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, switchMap, tap, throwError } from 'rxjs';
import { BaseService } from '../shared/services/base.service';


export interface InitResponse {
  retValue: string | number;
  retString: string;
  ScannerInfos: any[];
}

export interface CaptureResponse {
  retValue: string | number;
  retString: string;
}

export interface ImageDataResponse {
  retValue: string | number;
  retString: string;
  imageBase64: string;
}

export interface AbortResponse {
  retValue: string | number;
  retString: string;
}

export interface UninitResponse {
  retValue: string | number;
  retString: string;
}

export interface ScannerStatusResponse { // Add interface for scanner status
  IsCapturing: string | boolean,
  SensorOn: string | boolean,
  SensorValid: string | boolean,
  request: string,
  retString: string,
  retValue: string | number,
}

@Injectable({
  providedIn: 'root'
})

export class FingerprintService {

  private baseUrl: string;
  private deviceInfo: any;
  private pageId: string;

  constructor(
    private _http: HttpClient,
    private baseService: BaseService
  ) { }

  // Set the base URL dynamically (e.g., "http://localhost:5678/api")
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  // Set a unique page/session ID
  setPageId(id: string): void {
    this.pageId = id;
  }

  // Get the device info
  getDeviceInfo(): any {
    return this.deviceInfo;
  }

  // Store device info for later API calls
  setDeviceInfo(info: any): void {
    this.deviceInfo = info;
  }

  initDevice(): Observable<InitResponse> {
    let url = `${this.baseUrl}/initDevice?dummy=${Math.random()}`;
    return this._http.get<InitResponse>(url);
  }

  abortCapture(): Observable<AbortResponse> {
    if(!this.deviceInfo || !this.deviceInfo.Device){
      console.warn("Device not initialized");
      return throwError(() => new Error("Device not initialized or DeviceHandle missing for abortCapture"));
    }

    let url =  `${this.baseUrl}/abortCapture?dummy=${Math.random()}`;
    let params = new HttpParams()
      .set('sHandle', this.deviceInfo?.DeviceHandle)
      .set('resetTimer', 30000);

    return this._http.get<AbortResponse>(url, {params});
  }

  getScannerStatus(): Observable<ScannerStatusResponse> {
    if(!this.deviceInfo){
      console.warn("Device not initialized, getScannerStatus skipped.");
      return throwError(() => new Error("Device not initialized or device info missing for getScannerStatus"));
    }
    const url = `${this.baseUrl}/getScannerStatus?dummy=${Math.random()}`;
    const params = new HttpParams()
      .set('sHandle', this.deviceInfo?.DeviceHandle)
      .set('sIndex', this.deviceInfo?.DeviceIndex);

    return this._http.get<ScannerStatusResponse>(url, { params });
  }

  getPreviewImageData(): Observable<string> {
     if(!this.deviceInfo){
      console.warn("Device not initialized");
      return throwError(() => new Error("Device not initialized or DeviceHandle missing for getPreviewImageData"));
    }
     const url = `${this.baseUrl}/img/PreviewImg.bmp?dummy=${Math.random()}`;
     const params = new HttpParams() 
        .set('shandle', this.deviceInfo?.DeviceHandle)
        .set('id', this.pageId);

    return this._http.get(url, { params, responseType: 'blob' }).pipe(
      
      switchMap(blob => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        return new Observable<string>(observer => {
            reader.onloadend = () => {
                const base64data = reader.result as string;
                observer.next(base64data.split(',')[1]); 
                observer.complete();
            };
            reader.onerror = (error) => {
                observer.error(error);
            };
        });
      })
    );
  }


  captureSingle(): Observable<CaptureResponse> {
    if (!this.deviceInfo || !this.deviceInfo.DeviceHandle) {
      console.warn("Device not initialized or DeviceHandle missing for captureSingle. Returning error Observable.");
      return throwError(() => new Error("Device not initialized or DeviceHandle missing for captureSingle"));
    }

    let url = `${this.baseUrl}/captureSingle?dummy=${Math.random()}`;
    let params = new HttpParams()
    .set('sHandle', this.deviceInfo?.DeviceHandle)
    .set('id', this.pageId)
    .set('resetTimer', 30000);

    return this._http.get<CaptureResponse>(url, {params});
  }

  // startCapture instead of captureSingle
  startCapture(): Observable<CaptureResponse> {
    if (!this.deviceInfo || !this.deviceInfo.DeviceHandle) {
      console.warn("Device not initialized or DeviceHandle missing for startCapture. Returning error Observable.");
      return throwError(() => new Error("Device not initialized or DeviceHandle missing for startCapture"));
    }

    let url = `${this.baseUrl}/startCapturing?dummy=${Math.random()}`;
    let params = new HttpParams()
    .set('sHandle', this.deviceInfo?.DeviceHandle)
    .set('id', this.pageId)
    .set('resetTimer', 3000);
    return this._http.get<CaptureResponse>(url, {params});
  }

  // Retrieve the captured image data
  getImageData(fileType: number, width: number, height: number, compressionRatio: number): Observable<ImageDataResponse> {
    if (!this.deviceInfo || !this.deviceInfo.DeviceHandle) {
      console.warn("Device not initialized or DeviceHandle missing for getImageData. Returning error Observable.");
      return throwError(() => new Error("Device not initialized or DeviceHandle missing for getImageData"));
    }
    const url = `${this.baseUrl}/getImageData?dummy=${Math.random()}`;
    const params = new HttpParams()
      .set('sHandle', this.deviceInfo?.DeviceHandle)
      .set('id', this.pageId)
      .set('fileType', fileType.toString())
      .set('width', width.toString())
      .set('height', height.toString())
      .set('compressionRatio', compressionRatio.toString());
    return this._http.get<ImageDataResponse>(url, { params });
  }

  getData(id: string): Observable<any> {
    return this.baseService.get("GET_BY_ID", `id:${id}`, "fingerprint", "aimlsuit").pipe(
      map((response: any) => {
        if (response && response.resource && response.resource.length > 0) {
          return response.resource[0];
        } else {
          throw new Error('No data found');
        }
      }),
      catchError((error: any) => {
        console.error('Error fetching data:', error);
        return throwError(error);
      })
    );
  }

  uninitDevice(): Observable<UninitResponse> {
    if(!this.deviceInfo){
      console.warn("Device not initialized");
      return throwError(() => new Error("Device not initialized for uninitDevice"));
    }
    
    const url = `${this.baseUrl}/uninitDevice?dummy=${Math.random()}`;
  
    return this._http.get<UninitResponse>(url).pipe(
      tap(() => {
        this.deviceInfo = undefined;
        this.pageId = undefined;
      })
    );
  }
}
